import React, { useEffect, useState } from 'react';
import ApiService from "../../services/ApiService";
import Loader from "../../components/Loader";
import { formatCurrency, formatDate } from '../../utils/helper';
import { toast } from 'react-toastify';

const ExpenseReport = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [totalExpenses, setTotalExpenses] = useState(0);
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: ''
    });

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/reports/expenses',
                data: filters
            });
            if (response.data.status) {
                setData(response.data.data.expenses);
                setTotalExpenses(response.data.data.total_expenses);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch expense report");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchReport();
    };

    const handleExport = () => {
        const csvRows = [];
        const headers = ['Title', 'Category', 'Date', 'Amount'];
        csvRows.push(headers.join(','));

        data.forEach(item => {
            const values = [
                `"${item.title || ''}"`,
                `"${item.category || ''}"`,
                `"${formatDate(item.date)}"`,
                `"${item.amount}"`
            ];
            csvRows.push(values.join(','));
        });

        const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `expense_report_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Expense Report
                        </h1>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <form onSubmit={handleSearch} className="d-flex align-items-center position-relative my-1 gap-3">
                                    <div className="d-flex align-items-center position-relative">
                                        <input
                                            type="date"
                                            name="start_date"
                                            className="form-control form-control-solid w-150px"
                                            value={filters.start_date}
                                            onChange={handleFilterChange}
                                        />
                                    </div>
                                    <div className="d-flex align-items-center position-relative">
                                        <input
                                            type="date"
                                            name="end_date"
                                            className="form-control form-control-solid w-150px"
                                            value={filters.end_date}
                                            onChange={handleFilterChange}
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-base">Filter</button>
                                </form>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-3 align-items-center">
                                    <button 
                                        type="button" 
                                        className="btn btn-light-primary btn-sm"
                                        onClick={handleExport}
                                        disabled={data.length === 0}
                                    >
                                        <i className="fas fa-download me-2"></i> Export CSV
                                    </button>
                                    <div className="fw-bold fs-5 text-gray-800 border border-gray-200 p-2 rounded">
                                        Total Expenses: {formatCurrency(totalExpenses)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body py-4">
                            {loading ? <Loader /> : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th>Title</th>
                                                <th>Category</th>
                                                <th>Date</th>
                                                <th className="text-end">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {data.length > 0 ? (
                                                data.map((expense) => (
                                                    <tr key={expense.id}>
                                                        <td>{expense.title}</td>
                                                        <td>{expense.category || '-'}</td>
                                                        <td>{formatDate(expense.date)}</td>
                                                        <td className="text-end">{formatCurrency(expense.amount)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center">No records found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpenseReport;
